/**
 * MultiVmDummyTopology — TEMPORARY "coming soon" placeholder.
 */
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Network, X } from "lucide-react";
import PropTypes from "prop-types";

/** Static, decorative node/edge layout — purely illustrative, not real data. */
const DUMMY_NODES = [
  { id: "fw", label: "Firewall", x: 200, y: 40 },
  { id: "web", label: "Web Server", x: 90, y: 150 },
  { id: "app", label: "App Server", x: 310, y: 150 },
  { id: "db", label: "Database", x: 200, y: 250 },
];

const DUMMY_EDGES = [
  ["fw", "web"],
  ["fw", "app"],
  ["web", "db"],
  ["app", "db"],
];

function nodeById(id) {
  return DUMMY_NODES.find((n) => n.id === id);
}

export default function MultiVmDummyTopology({ title }) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!show) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setShow(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show]);

  const handleOpen = (event) => {
    event?.stopPropagation();
    setShow(true);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setShow(false);
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-1000 flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="flex flex-col w-[90vw] max-w-4xl h-[80vh] rounded-sm border border-(--color-border) bg-(--color-bg-card) shadow-[0_0_20px_var(--color-shadow-primary)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div className="flex items-center justify-between border-b border-(--color-border) px-6 py-4 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Network className="h-5 w-5 text-(--color-primary) shrink-0" />
            <h3 className="text-lg font-semibold text-(--color-text-white) truncate">
              {title || "Topology preview"}
            </h3>
            <span className="inline-flex items-center rounded-full border border-violet-400/50 bg-violet-400/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-300">
              Coming Soon
            </span>
          </div>
          <button
            type="button"
            className="flex h-9 w-9 items-center justify-center text-(--color-text-muted) transition-colors hover:text-(--color-primary)"
            onClick={handleClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* ── Dummy topology illustration ── */}
        <div className="relative flex-1 min-h-0 p-6">
          <svg
            viewBox="0 0 400 300"
            preserveAspectRatio="xMidYMid meet"
            className="w-full h-full rounded-sm border border-(--color-border) bg-(--color-bg)"
            aria-hidden="true"
          >
            {DUMMY_EDGES.map(([from, to]) => {
              const a = nodeById(from);
              const b = nodeById(to);
              return (
                <line
                  key={`${from}-${to}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="var(--color-primary)"
                  strokeWidth="2"
                  strokeOpacity="0.4"
                  strokeDasharray="6 4"
                />
              );
            })}
            {DUMMY_NODES.map((n) => (
              <g key={n.id}>
                <rect
                  x={n.x - 55}
                  y={n.y - 16}
                  width="110"
                  height="32"
                  rx="6"
                  fill="var(--color-bg-card)"
                  stroke="var(--color-primary)"
                  strokeOpacity="0.5"
                />
                <text
                  x={n.x}
                  y={n.y + 5}
                  textAnchor="middle"
                  fontSize="13"
                  fill="var(--color-text-muted)"
                >
                  {n.label}
                </text>
              </g>
            ))}
          </svg>

          {/* Overlay message */}
          <div className="absolute inset-6 flex items-center justify-center rounded-sm bg-(--color-bg-card)/70 backdrop-blur-xs">
            <div className="text-center space-y-2 px-6">
              <p className="text-base font-bold text-violet-300">
                Topology preview coming soon
              </p>
              <p className="text-sm max-w-sm mx-auto">
                This is a placeholder topology. Real network diagrams for
                Multi-VM labs are on the way.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        className="cursor-pointer inline-flex items-center justify-center p-1.5 rounded-lg border border-slate-700 bg-slate-900 text-slate-300 hover:text-(--color-primary) hover:border-(--color-primary) transition"
        onClick={handleOpen}
        title="Preview Topology (coming soon)"
      >
        <Network className="h-4.5 w-4.5" />
      </button>
      {show && createPortal(modalContent, document.body)}
    </>
  );
}

MultiVmDummyTopology.propTypes = {
  title: PropTypes.string,
};
